import React, { useEffect } from "react";
import styled from "styled-components";
import "./v2styles.css";
import SEO from "../../components/SEO";
import { faaomptSEO } from "../../data/seo";
import BreadcrumbSchema from "../../components/BreadcrumbSchema";
import { useLoading } from "../../components/LoaderProvider";
import { ContactButton } from "./Header";
import { CheckedClipboardIcon } from "./Icons";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Hero = styled.div`
  background-image: url("/Marble-dark.jpg");
  background-size: cover;
  width: 100%;
  padding: 80px 20px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const HeroTitle = styled.h1`
  max-width: 800px;

  && {
    color: white;
  }
`;

const HeroSubtitle = styled.p`
  color: white;
  font-size: 18px;
  max-width: 650px;
  line-height: 1.6;
  margin-top: 16px;
`;

const SageSeparator = styled.div`
  width: 108px;
  height: 3px;
  border-top: 3px solid #aecdc2;
  margin: 24px 0;
`;

const Section = styled.div`
  max-width: 800px;
  padding: 60px 20px;
  border-bottom: 1px solid #dcdcdc;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: 30px;
  margin-bottom: 20px;
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 16px;
`;

const CredentialList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CredentialListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  font-size: 16px;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const CtaSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px 100px;
`;

const CtaTitle = styled.h2`
  font-size: 28px;
  margin-bottom: 24px;
`;

const StatHighlight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  background: #f6e3fe33;
  border-left: 4px solid #515398;
  border-radius: 8px;
  padding: 20px 24px;
  margin: 24px 0;
`;

const StatNumber = styled.span`
  font-size: 48px;
  font-weight: bold;
  font-style: italic;
  font-family: "Brygada 1918", serif;
  color: #515398;
`;

const StatCaption = styled.span`
  font-size: 15px;
  line-height: 1.5;
`;

const FaaomptV2 = () => {
  const { setIsLoading } = useLoading();

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(false);
  }, [setIsLoading]);

  return (
    <>
      <SEO {...faaomptSEO} />
      <BreadcrumbSchema currentPage="What Is FAAOMPT?" path="/faaompt" />
      <PageContainer>
        <Hero>
          <HeroTitle>What Is FAAOMPT?</HeroTitle>
          <SageSeparator />
          <HeroSubtitle>
            The highest orthopedic manual therapy credential in the field -
            and why it matters when you're choosing who treats your pain.
          </HeroSubtitle>
        </Hero>

        <Section>
          <SectionTitle>The credential, explained</SectionTitle>
          <Paragraph>
            FAAOMPT stands for Fellow of the American Academy of Orthopaedic
            Manual Physical Therapists. It's a post-doctoral fellowship - not
            a weekend course or a certificate you can add after a single
            seminar. Earning it takes roughly 1,000 hours of additional,
            mentored training in advanced orthopedic evaluation and
            hands-on manual therapy, completed after a therapist already
            holds their Doctor of Physical Therapy degree.
          </Paragraph>
          <StatHighlight>
            <StatNumber>2%</StatNumber>
            <StatCaption>
              Only about 2% of physical therapists nationwide hold the
              FAAOMPT fellowship - the credential Dr. Jackie West earned in
              2021.
            </StatCaption>
          </StatHighlight>
          <Paragraph>
            Dr. Jackie West earned her fellowship through The Manual Therapy
            Institute in 2021, several years into practicing after her 2016
            Doctor of Physical Therapy degree from the University of St.
            Augustine.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>How it's different from general manual therapy</SectionTitle>
          <Paragraph>
            Any physical therapist can call themselves a manual therapist
            after learning hands-on techniques in school or a short course.
            An FAAOMPT has gone much further: years of mentored,
            case-by-case training focused specifically on tracing pain back
            to where it actually starts, rather than only treating the spot
            where it's felt.
          </Paragraph>
          <Paragraph>
            That's the difference patients notice most - a knee problem that
            turns out to be a hip problem, or shoulder pain that traces back
            to the neck. Fellowship training is built around exactly that
            kind of root-cause diagnosis, paired with the advanced hands-on
            treatment to address it.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Dr. West's credentials</SectionTitle>
          <CredentialList>
            <CredentialListItem>
              <CheckedClipboardIcon />
              Doctor of Physical Therapy, University of St. Augustine,
              Austin - 2016
            </CredentialListItem>
            <CredentialListItem>
              <CheckedClipboardIcon />
              Fellowship in the American Academy of Orthopedic Manual
              Physical Therapists, through The Manual Therapy Institute -
              2021
            </CredentialListItem>
            <CredentialListItem>
              <CheckedClipboardIcon />
              Contributing faculty member, University of St. Augustine
              physical therapy program - 2022 to present
            </CredentialListItem>
          </CredentialList>
        </Section>

        <CtaSection>
          <CtaTitle>Experience fellowship-level care, at home.</CtaTitle>
          <ContactButton to="/contact">CONTACT US</ContactButton>
        </CtaSection>
      </PageContainer>
    </>
  );
};

export default FaaomptV2;
